import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';

import decodeJwtToken from '../auth/decodeJwtToken';
import { getSystemAccessRoute } from '../shared/systemAccessRouting';

const AuthContext = createContext(null);

const brandColor = '#800b33';
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const WARNING_WINDOW_MS = 60 * 1000;
const ACTIVITY_STORAGE_KEY = 'savvy:last-activity-at';
const AUTH_REDIRECT_PATH = '/login';
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

const AuthLoadingScreen = () => (
  <Box
    sx={{
      minHeight: '60vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      background: 'linear-gradient(135deg, #2a0a16, #800b33)',
      color: '#f8fafc',
    }}
  >
    <CircularProgress size={64} thickness={4.5} sx={{ color: brandColor }} />
    <Typography variant="body1" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 500 }}>
      Tunakuthibitishia akaunti yako...
    </Typography>
  </Box>
);

const bootstrapUser = () => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    return null;
  }

  try {
    const decoded = decodeJwtToken(accessToken);
    if (decoded && decoded.exp * 1000 > Date.now()) {
      return decoded;
    }
  } catch (error) {
    console.error('Error decoding token:', error);
  }

  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem(ACTIVITY_STORAGE_KEY);
  return null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => bootstrapUser());
  const [loading, setLoading] = useState(false);
  const [idleWarningOpen, setIdleWarningOpen] = useState(false);
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  const redirectToLogin = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname !== AUTH_REDIRECT_PATH) {
      window.location.replace(AUTH_REDIRECT_PATH);
    }
  }, []);

  const loadUserFromToken = useCallback((showSpinner = false) => {
    if (showSpinner) {
      setLoading(true);
    }

    const decodedUser = bootstrapUser();
    setUser(decodedUser);

    if (showSpinner) {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearTimers();
    setIdleWarningOpen(false);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(ACTIVITY_STORAGE_KEY);
    setUser(null);
  }, [clearTimers]);

  const expireSession = useCallback(() => {
    logout();
    redirectToLogin();
  }, [logout, redirectToLogin]);

  const markActivity = useCallback(() => {
    if (!localStorage.getItem('accessToken')) return;
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(Date.now()));
    setIdleWarningOpen(false);
  }, []);

  const scheduleIdleTimeout = useCallback(() => {
    clearTimers();

    if (!localStorage.getItem('accessToken')) {
      return;
    }

    const lastActivityAt = Number(localStorage.getItem(ACTIVITY_STORAGE_KEY) || Date.now());
    const elapsed = Date.now() - lastActivityAt;
    const remaining = IDLE_TIMEOUT_MS - elapsed;

    if (remaining <= 0) {
      expireSession();
      return;
    }

    if (remaining <= WARNING_WINDOW_MS) {
      setIdleWarningOpen(true);
    } else {
      warningTimerRef.current = window.setTimeout(() => {
        setIdleWarningOpen(true);
      }, remaining - WARNING_WINDOW_MS);
    }

    idleTimerRef.current = window.setTimeout(() => {
      expireSession();
    }, remaining);
  }, [clearTimers, expireSession]);

  const validateSessionFreshness = useCallback(() => {
    if (!localStorage.getItem('accessToken')) {
      clearTimers();
      setIdleWarningOpen(false);
      setUser(null);
      return;
    }

    const lastActivityAt = Number(localStorage.getItem(ACTIVITY_STORAGE_KEY) || 0);
    if (lastActivityAt && Date.now() - lastActivityAt >= IDLE_TIMEOUT_MS) {
      expireSession();
      return;
    }

    loadUserFromToken();
    scheduleIdleTimeout();
  }, [clearTimers, expireSession, loadUserFromToken, scheduleIdleTimeout]);

  useEffect(() => {
    const handleFocus = () => validateSessionFreshness();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [validateSessionFreshness]);

  useEffect(() => {
    if (!user) {
      clearTimers();
      setIdleWarningOpen(false);
      return undefined;
    }

    markActivity();
    scheduleIdleTimeout();

    const onActivity = () => {
      markActivity();
      scheduleIdleTimeout();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateSessionFreshness();
      }
    };

    const onStorage = (event) => {
      if (!event.key || ['accessToken', 'refreshToken', ACTIVITY_STORAGE_KEY].includes(event.key)) {
        validateSessionFreshness();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('storage', onStorage);

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [user, clearTimers, markActivity, scheduleIdleTimeout, validateSessionFreshness]);

  const login = useCallback((accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(Date.now()));
    setIdleWarningOpen(false);
    loadUserFromToken();
    scheduleIdleTimeout();
  }, [loadUserFromToken, scheduleIdleTimeout]);

  const staySignedIn = useCallback(() => {
    markActivity();
    scheduleIdleTimeout();
  }, [markActivity, scheduleIdleTimeout]);

  const authState = useMemo(() => ({
    user,
    role: user?.role,
    memberId: user?.member_id,
    groupId: user?.group_id,
    groupName: user?.group_name,
    systemAccess: {
      nextRequiredAction: user?.next_required_action || null,
      requiresTermsAcceptance: Boolean(user?.requires_terms_acceptance),
      requiresRegistrationFee: Boolean(user?.requires_registration_fee),
      isRestrictedForAnnualFee: Boolean(user?.is_restricted_for_annual_fee),
      hasFullAccess: Boolean(user?.has_full_access),
      restrictionMessage: user?.restriction_message || '',
      registrationFee: user?.registration_fee || null,
      annualFee: user?.annual_fee || null,
    },
    systemAccessRoute: getSystemAccessRoute(user),
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    refreshUser: loadUserFromToken,
  }), [user, loading, login, logout, loadUserFromToken]);

  if (loading) {
    return <AuthLoadingScreen />;
  }

  return (
    <AuthContext.Provider value={authState}>
      {children}
      <Snackbar
        open={idleWarningOpen && !!user}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') {
            setIdleWarningOpen(false);
          }
        }}
      >
        <Alert
          severity="warning"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
          action={
            <Button color="inherit" size="small" onClick={staySignedIn} sx={{ fontWeight: 700 }}>
              Stay signed in
            </Button>
          }
        >
          Your session will end in less than 1 minute due to inactivity.
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
